import {
  ExtrasContent,
  ContentMap,
  ContentRootTree,
  Content,
  ContentType,
  PurchaseInfo,
  Bundle,
} from './extras.domain';
import {
  GetProfileQuery,
  ContentIds as GraphQLContentIds,
  GetContentQuery,
  ContentAccessMethod,
  BundleStatus,
  ContentVideoValue,
} from 'generated/graphql';
import { ArrayItem, TMap } from '@client/new/helpers/types';
import { ProfileData } from '@design-system/components/mobile-preview/types';
import { toChildrenMap } from './tree';

export type GQLContent = GetContentQuery['content'];
export type GQLContentItems = GQLContent['contentItems'];

export const toExtrasContent = (
  c: GetContentQuery['content'],
): ExtrasContent => {
  const contentIds: ContentRootTree = toContentIds(c.contentIds);
  return {
    contentIds,
    contentMap: toContentMap(c.contentItems),
    childrenMap: toChildrenMap(contentIds),
  };
};

const toContentIds = (
  c: GraphQLContentIds | undefined | null,
): ContentRootTree => {
  if (!c?.id) return { id: 'empty' };

  const children = (c.items || [])
    .map(toContentIds)
    .filter((item): item is ContentRootTree => !!item);

  return {
    id: c.id,
    ...(children.length > 0 ? { items: children } : {}),
  };
};

/**
 * Maps GraphQL backend data to domain PurchaseInfo type.
 * Uses real backend data - no mocked values.
 *
 * Phase 2, Step 2.1: New MVVM-compliant mapper
 *
 * @param v - GraphQL content item
 * @returns PurchaseInfo domain object
 */
export const toPurchaseInfo = (v: ArrayItem<GQLContentItems>): PurchaseInfo => {
  const { value } = v;
  if (
    value.__typename === 'ContentTabValue' ||
    value.__typename === 'ContentImageValue'
  ) {
    return { type: 'not-purchasable' };
  }

  // All other types have accessMethod and bundles from backend
  // Use real backend data - no mocking!
  type PurchasableInfo = Extract<PurchaseInfo, { type: 'purchasable' }>;

  const accessMethodMap: Record<
    ContentAccessMethod,
    PurchasableInfo['accessMethod']
  > = {
    [ContentAccessMethod.Default]: 'Default',
    [ContentAccessMethod.BundlesOnly]: 'BundlesOnly',
    [ContentAccessMethod.SubscriptionAndBundles]: 'SubscriptionAndBundles',
  };

  // Handle ContentVideoValue with aliased fields (videoAccessMethod, videoBundles)
  let accessMethodValue: ContentAccessMethod | null | undefined;
  let bundlesArray: GQLBundle[] | undefined;

  if (value.__typename === 'ContentVideoValue') {
    const videoValue = value as ContentVideoValue & {
      videoAccessMethod?: ContentAccessMethod | null;
      videoBundles?: GQLBundle[];
    };
    accessMethodValue = videoValue.videoAccessMethod;
    bundlesArray = videoValue.videoBundles;
  } else {
    accessMethodValue =
      'accessMethod' in value ? value.accessMethod : undefined;
    bundlesArray = 'bundles' in value ? value.bundles : undefined;
  }

  const accessMethod = accessMethodValue
    ? accessMethodMap[accessMethodValue as ContentAccessMethod]
    : 'Default';

  // Map backend bundles to domain bundles
  type GQLBundle = {
    id: number;
    title: string;
    status: BundleStatus;
    wasPurchased: boolean;
  };
  const bundles: Bundle[] =
    bundlesArray && Array.isArray(bundlesArray)
      ? (bundlesArray as GQLBundle[]).map((bundle) => ({
          id: bundle.id,
          title: bundle.title,
          status:
            bundle.status === BundleStatus.Published
              ? ('published' as const)
              : bundle.status === BundleStatus.Archived
              ? ('archived' as const)
              : ('draft' as const),
          wasPurchased: bundle.wasPurchased,
        }))
      : [];

  // Compute publication status from backend fields
  const isArchived = 'isArchived' in value ? value.isArchived ?? false : false;
  const publicationStatus = isArchived
    ? ('archived' as const)
    : value.isPublished
    ? ('published' as const)
    : ('draft' as const);

  return {
    type: 'purchasable',
    accessMethod,
    bundles,
    publicationStatus,
  };
};

const toContent = (v: ArrayItem<GQLContentItems>): Content => {
  const {
    type,
    value: { __typename, ...value },
  } = v;

  // todo: handle null values from the API properly
  type ValueWithOptionalStrings = Record<string, unknown> & {
    coverURL?: string | null;
    coverImage?: string | null;
  };
  const valueWithStrings = value as ValueWithOptionalStrings;
  if (valueWithStrings.coverURL === null) valueWithStrings.coverURL = '';
  if (valueWithStrings.coverImage === null) valueWithStrings.coverImage = '';

  const valueWithPurchaseInfo = {
    ...value,
    purchaseInfo: toPurchaseInfo(v),
  };

  return { type, value: valueWithPurchaseInfo } as Content;
};

const toContentMap = (items: GQLContentItems) =>
  items.reduce(
    (acc, item) => ({ ...acc, [item.value.id]: toContent(item) }),
    {} as ContentMap,
  );

export const rootAllowedTypes: Array<ContentType> = ['ContentTab'];

export const contentHierarchyRules: Partial<TMap<ContentType, ContentType[]>> =
  {
    ['ContentTab']: [
      'ContentLegacyGuide',
      'ContentLink',
      'ContentSlides',
      'ContentPDF',
    ],
    ['ContentSlides']: ['ContentImage'],
  };

export const toProfileData = (p: GetProfileQuery['creator']): ProfileData => {
  if (!p) return {};
  return {
    firstName: p.firstName,
    lastName: p.lastName,
    title: p.title,
    profileInstagram: p.profileInstagram,
    profileYoutubeChannel: p.profileYoutubeChannel,
    profileTiktok: p.profileTiktok,
    mediaBanner: p.mediaBanner,
    mediaAvatar: p.mediaAvatar,
    customColorSchema: {
      primaryColor: p.customColorSchema?.primaryColor,
    },
    offering: p.offering,
  };
};
